import { EmbedWidgetConfig } from '@finxone-platform/shared/sys-config-types';
import {
  CustomWidgetEdgeConfig,
  browserCapabilitiesAllow,
  buildCustomWidgetEdgeUrl,
  intersectBrowserCapabilities,
} from '@finxone-platform/shared/app-sdk';
import { CustomWidgetInstanceProperties } from './custom-widget-host.utils';

export interface CustomWidgetFrameResolution {
  src: string;
  sandbox: 'allow-scripts' | 'allow-scripts allow-same-origin';
  allow: string | null;
  targetOrigin: string;
}

/** Pure fail-closed resolver shared by the component tests and production render path. */
export function resolveCustomWidgetFrame(
  instance: CustomWidgetInstanceProperties,
  activation: EmbedWidgetConfig | undefined,
  edge: CustomWidgetEdgeConfig | undefined,
  browserCapabilitiesEnabled: boolean,
): CustomWidgetFrameResolution | undefined {
  const browser = intersectBrowserCapabilities(instance.grants?.browser, activation?.grants?.browser);
  if (browserCapabilitiesEnabled && browser.length > 0 && activation?.originKey && edge) {
    try {
      const src = buildCustomWidgetEdgeUrl(edge, activation.originKey, instance.widgetId, instance.version);
      return {
        src,
        sandbox: 'allow-scripts allow-same-origin',
        allow: browserCapabilitiesAllow(browser),
        targetOrigin: new URL(src).origin,
      };
    } catch {
      // Invalid/missing deployment data must never widen a frame. Fall through to opaque.
    }
  }

  const src = instance.bundleUrl || opaqueWidgetUrl(instance);
  return src ? { src, sandbox: 'allow-scripts', allow: null, targetOrigin: '*' } : undefined;
}

function opaqueWidgetUrl(instance: CustomWidgetInstanceProperties): string {
  if (!instance.widgetId || !instance.version) return '';
  const root = instance.grants?.network?.length ? '/assets/custom-widgets-net' : '/assets/custom-widgets';
  return `${root}/${encodeURIComponent(instance.widgetId)}/${encodeURIComponent(instance.version)}/runner.html`;
}
